import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Checkbox, FormControlLabel, CircularProgress, Paper, Typography, IconButton, Card, CardActionArea, Chip } from '@mui/material';
import { fetchOrganizations, fetchCalendarClips } from '../api/apiService';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

const liveFormatMap = { 1: '3D', 2: '2D', 3: '电台', 4: '联动', 5: '双播', 6: '其它' };
const liveContentMap = { 1: '才艺', 2: '杂谈', 3: '游戏', 4: '综艺', 5: '活动', 6: '观影', 7: '特殊', 8: '投稿', 9: '歌回', 10: '其它' };

// Organization colors
const orgColors = {
    1: { bg: '#FFF3E0', border: '#F57C00', text: '#E65100', main: '#F57C00' }, // 柚恩
    2: { bg: '#E8F5E9', border: '#388E3C', text: '#1B5E20', main: '#388E3C' }, // 露早
    3: { bg: '#E3F2FD', border: '#1976D2', text: '#0D47A1', main: '#1976D2' }, // Default/Other
};

const formatDateParam = (date) => {
    const pad = (num) => num.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const normalizeRedirectUrl = (url = '') => {
    const trimmed = String(url).trim();
    if (!trimmed) return '#';
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed.replace(/^\/+/, '')}`;
};

const Calendar = () => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [allClipsData, setAllClipsData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [organizations, setOrganizations] = useState([]);
    const [filters, setFilters] = useState({});

    useEffect(() => {
        const fetchOrgs = async () => {
            try {
                const orgs = await fetchOrganizations();
                setOrganizations(orgs);

                const initialFilters = {};
                orgs.forEach(org => {
                    initialFilters[org.id] = true;
                });
                setFilters(initialFilters);
            } catch (error) {
                console.error("无法加载社团数据:", error);
            }
        };
        fetchOrgs();
    }, []);

    const fetchCalendarData = useCallback(async () => {
        setLoading(true);
        try {
            const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
            const end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
            const response = await fetchCalendarClips({
                start_date: formatDateParam(start),
                end_date: formatDateParam(end),
            });

            const processedClips = (response.clips || []).map(clip => ({
                ...clip,
                date: new Date(clip.datetime),
                redirectUrl: normalizeRedirectUrl(clip.redirectUrl)
            }));

            setAllClipsData(processedClips);
        } catch (error) {
            console.error("无法加载日程数据:", error);
        } finally {
            setLoading(false);
        }
    }, [currentDate]);

    useEffect(() => {
        fetchCalendarData();
    }, [fetchCalendarData]);

    const handleFilterChange = (event) => {
        setFilters({
            ...filters,
            [event.target.name]: event.target.checked,
        });
    };

    const filteredClips = useMemo(() => {
        if (!allClipsData || allClipsData.length === 0) return [];
        return allClipsData.filter(clip => filters[clip.organizationId]);
    }, [allClipsData, filters]);

    const renderCalendar = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const today = new Date();

        const firstDayOfMonth = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const days = [];
        const weekdays = ['日', '一', '二', '三', '四', '五', '六'];
        
        // Weekday headers
        weekdays.forEach(day => {
            days.push(
                <Box key={day} sx={{ 
                    p: 2, 
                    textAlign: 'center', 
                    fontWeight: 'bold', 
                    color: 'text.secondary',
                    bgcolor: 'background.default',
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                }}>
                    {day}
                </Box>
            );
        });

        // Empty cells for previous month
        for (let i = 0; i < firstDayOfMonth; i++) {
            days.push(<Box key={`empty-${i}`} sx={{ bgcolor: 'background.default', minHeight: 120, borderRight: '1px solid', borderBottom: '1px solid', borderColor: 'divider', opacity: 0.5 }} />);
        }

        // Days
        for (let day = 1; day <= daysInMonth; day++) {
            const dayClips = filteredClips.filter(clip => {
                const clipDate = clip.date;
                return clipDate.getFullYear() === year && clipDate.getMonth() === month && clipDate.getDate() === day;
            }).sort((a, b) => a.date - b.date);

            const isToday = year === today.getFullYear() && month === today.getMonth() && day === today.getDate();
            
            days.push(
                <Box key={day} sx={{ 
                    minHeight: 120, 
                    p: 1, 
                    borderRight: '1px solid', 
                    borderBottom: '1px solid', 
                    borderColor: 'divider',
                    bgcolor: 'background.paper',
                    position: 'relative'
                }}>
                    <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        width: 28, 
                        height: 28, 
                        borderRadius: '50%', 
                        bgcolor: isToday ? 'secondary.main' : 'transparent',
                        color: isToday ? 'white' : 'text.primary',
                        fontWeight: isToday ? 'bold' : 'normal',
                        mb: 1
                    }}>
                        {day}
                    </Box>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {dayClips.map(clip => {
                            const colors = orgColors[clip.organizationId] || orgColors[3];
                            return (
                                <Card 
                                    key={`${clip.authorId}-${clip.datetime}-${clip.title}-${clip.redirectUrl}`} 
                                    sx={{ 
                                        bgcolor: colors.bg, 
                                        borderLeft: `4px solid ${colors.border}`,
                                        boxShadow: 'none',
                                        '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.1)', transform: 'translateY(-1px)' },
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    <CardActionArea
                                        component="a"
                                        href={clip.redirectUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{ p: 1 }}
                                    >
                                        <Typography variant="caption" display="block" sx={{ fontWeight: 'bold', color: colors.text, mb: 0.5, lineHeight: 1.2 }}>
                                            {clip.date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false })} - {clip.title}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                            <Chip 
                                                label={liveFormatMap[clip.live_format] || '未知'} 
                                                size="small" 
                                                sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(255,255,255,0.6)' }} 
                                            />
                                            <Chip 
                                                label={liveContentMap[clip.live_content] || '未知'} 
                                                size="small" 
                                                sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'rgba(255,255,255,0.6)' }} 
                                            />
                                        </Box>
                                    </CardActionArea>
                                </Card>
                            );
                        })}
                    </Box>
                </Box>
            );
        }
        return days;
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ 
                p: 3, 
                bgcolor: 'primary.main', 
                color: 'primary.contrastText',
                display: 'flex',
                flexDirection: 'column',
                gap: 2
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <IconButton onClick={() => setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))} sx={{ color: 'white' }}>
                        <ArrowBackIosNewIcon />
                    </IconButton>
                    <Typography variant="h5" fontWeight="bold">
                        {`${currentDate.getFullYear()}年 ${currentDate.getMonth() + 1}月`}
                    </Typography>
                    <IconButton onClick={() => setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))} sx={{ color: 'white' }}>
                        <ArrowForwardIosIcon />
                    </IconButton>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}>
                    {organizations.map(org => {
                        const colors = orgColors[org.id] || orgColors[3];
                        return (
                            <FormControlLabel
                                key={org.id}
                                control={
                                    <Checkbox
                                        checked={filters[org.id] || false}
                                        onChange={handleFilterChange}
                                        name={String(org.id)}
                                        sx={{ 
                                            color: 'rgba(255,255,255,0.7)', 
                                            '&.Mui-checked': { color: 'white' } 
                                        }}
                                    />
                                }
                                label={
                                    <Chip 
                                        label={org.name} 
                                        size="small" 
                                        sx={{ 
                                            bgcolor: colors.main, 
                                            color: 'white', 
                                            fontWeight: 'bold',
                                            border: '1px solid rgba(255,255,255,0.3)'
                                        }} 
                                    />
                                }
                            />
                        );
                    })}
                </Box>
            </Box>
            
            <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(7, 1fr)', 
                borderLeft: '1px solid', 
                borderTop: '1px solid', 
                borderColor: 'divider' 
            }}>
                {renderCalendar()}
            </Box>
        </Paper>
    );
};

export default Calendar;
